import {
  PhysicsAggregate,
  PhysicsShapeType,
  HavokPlugin,
  Vector3,
  type Scene,
  type Mesh,
  Observable,
  type Observer,
} from '@babylonjs/core';
import HavokPhysics from '@babylonjs/havok';
import type { ITableDef, Vec2 } from '../core/types';
import { DEFAULT_PHYSICS_CONFIG, type PhysicsConfig } from './PhysicsConfig';

// WASM file path (copied to public folder)
const HAVOK_WASM_URL = '/HavokPhysics.wasm';

/**
 * Physics manager using Havok plugin.
 *
 * Uses gravity-vector tilt mapping for realistic rolling physics:
 * - Forces applied in onBeforePhysicsObservable (per physics tick)
 * - Velocity clamping in onAfterPhysicsObservable (post-solve)
 * - No per-frame angular sync - let Havok handle rolling naturally
 */
export class PhysicsManager {
  private scene: Scene;
  private havokPlugin: HavokPlugin | null = null;
  private initialized = false;

  // Physics bodies
  private ballAggregate: PhysicsAggregate | null = null;
  private ballMesh: Mesh | null = null;
  private boardAggregate: PhysicsAggregate | null = null;
  private wallAggregates: PhysicsAggregate[] = [];
  private targetAggregates: PhysicsAggregate[] = [];
  private finishAggregate: PhysicsAggregate | null = null;
  private finishMesh: Mesh | null = null;

  // Events - emits target ID on collision
  public onTargetHitObservable = new Observable<string>();

  // Physics observable hooks
  private beforePhysicsObserver: Observer<Scene> | null = null;
  private afterPhysicsObserver: Observer<Scene> | null = null;

  // Reusable vectors to avoid per-frame allocations
  private readonly forceVector = new Vector3();
  private readonly resistanceVector = new Vector3();
  private readonly clampVector = new Vector3();
  private readonly zeroVector = new Vector3(0, 0, 0);

  // Cached tilt input (set per frame, used by physics tick)
  private cachedTilt: Vec2 = { x: 0, y: 0 };

  // State for boof tracking (one-shot mechanism)
  private lastTilt: Vec2 = { x: 0, y: 0 };
  private boofPending = false;
  private boofKickX = 0;
  private boofKickZ = 0;

  // Stop-to-rest tracking (time-based)
  private nearStillTime = 0;

  // Configuration
  readonly config: PhysicsConfig;
  readonly gravity: number;

  constructor(scene: Scene, config: PhysicsConfig = DEFAULT_PHYSICS_CONFIG) {
    this.scene = scene;
    this.config = config;
    this.gravity = 9.81 * config.worldUnitsPerMeter;
  }

  /** Initialize Havok physics (async - loads WASM) */
  async init(): Promise<void> {
    if (this.initialized) return;

    // Load WASM with explicit path
    const havokInstance = await HavokPhysics({
      locateFile: () => HAVOK_WASM_URL,
    });
    this.havokPlugin = new HavokPlugin(true, havokInstance);

    // Enable physics on scene with downward gravity
    this.scene.enablePhysics(
      new Vector3(0, -this.gravity, 0),
      this.havokPlugin
    );

    // Set up physics tick hooks for proper force/clamp timing
    this.setupPhysicsObservables();

    this.initialized = true;
  }

  /** Set up physics observable hooks for correct timing */
  private setupPhysicsObservables(): void {
    // Before physics tick: apply gravity tilt + rolling resistance + boof
    this.beforePhysicsObserver = this.scene.onBeforePhysicsObservable.add(() => {
      this.applyPhysicsForces();
    });

    // After physics tick: clamp velocities (post-solve safety)
    this.afterPhysicsObserver = this.scene.onAfterPhysicsObservable.add(() => {
      this.clampVelocitiesPostSolve();
    });
  }

  /**
   * Apply forces during physics tick (called by onBeforePhysicsObservable).
   * Uses cached tilt input set by setTiltInput().
   */
  private applyPhysicsForces(): void {
    if (!this.ballAggregate || !this.ballMesh) return;

    const body = this.ballAggregate.body;
    const tilt = this.cachedTilt;

    const cfg = this.config;

    // Apply deadzone
    const tiltMag = Math.sqrt(tilt.x * tilt.x + tilt.y * tilt.y);
    const effectiveTiltX = tiltMag < cfg.tiltPhysicsDeadzone ? 0 : tilt.x;
    const effectiveTiltY = tiltMag < cfg.tiltPhysicsDeadzone ? 0 : tilt.y;

    // --- 1) Tilt-driven force (slope simulation)
    // Map normalized input to board slope angle, then calculate rolling acceleration
    const maxAngleRad = (cfg.maxBoardAngleDeg * Math.PI) / 180;
    const angleX = effectiveTiltX * maxAngleRad;
    const angleY = effectiveTiltY * maxAngleRad;

    // Rolling sphere acceleration: a = g * sin(θ) * rollFactor
    // Signs for pinball camera orientation (positive Z at bottom of screen)
    const ax = -Math.sin(angleX) * this.gravity * cfg.rollFactor;
    const az = Math.sin(angleY) * this.gravity * cfg.rollFactor;

    // Apply force: F = m * a
    this.forceVector.set(ax * cfg.ball.mass, 0, az * cfg.ball.mass);
    body.applyForce(this.forceVector, this.ballMesh.absolutePosition);

    // --- 2) Two-regime rolling resistance
    const vel = body.getLinearVelocity();
    const planarSpeed = Math.sqrt(vel.x * vel.x + vel.z * vel.z);

    if (planarSpeed > 1e-4) {
      // Blend between linear (viscous) and constant resistance
      // Near-zero: viscous damping to prevent jitter (F ~ v)
      // At speed: constant rolling resistance (F ~ m*g*const)
      const linearFactor = Math.min(planarSpeed / cfg.rolling.linearSpeed, 1.0);
      const constForce = cfg.rolling.resistanceConst * this.gravity * cfg.ball.mass;
      const linForce = cfg.rolling.resistanceLin * planarSpeed * cfg.ball.mass;

      // Blend: more viscous near rest, more constant at speed
      const rrForceMag = constForce * linearFactor + linForce * (1 - linearFactor * 0.5);

      // Cap so resistance never reverses velocity in one tick
      const physicsDt = 1 / 60;
      const maxOppose = (planarSpeed * cfg.ball.mass) / physicsDt;
      const mag = Math.min(rrForceMag, maxOppose * 0.9);

      // Oppose planar velocity direction
      this.resistanceVector.set(
        (-vel.x / planarSpeed) * mag,
        0,
        (-vel.z / planarSpeed) * mag
      );
      body.applyForce(this.resistanceVector, this.ballMesh.absolutePosition);
    }

    // --- 3) Apply pending boof (one-shot, queued by setTiltInput)
    if (this.boofPending && cfg.boof.enabled) {
      // Apply as velocity kick
      const newVelX = vel.x + this.boofKickX;
      const newVelZ = vel.z + this.boofKickZ;
      this.clampVector.set(newVelX, vel.y, newVelZ);
      body.setLinearVelocity(this.clampVector);
      this.boofPending = false;
    }
  }

  /**
   * Clamp velocities after physics solve (called by onAfterPhysicsObservable).
   * Also handles time-based stop-to-rest.
   */
  private clampVelocitiesPostSolve(): void {
    if (!this.ballAggregate) return;

    const body = this.ballAggregate.body;
    const vel = body.getLinearVelocity();
    const angVel = body.getAngularVelocity();

    const cfg = this.config;

    // --- Velocity clamping (post-solve safety)
    const velSq = vel.lengthSquared();
    const maxVelSq = cfg.maxVelocity * cfg.maxVelocity;
    if (velSq > maxVelSq) {
      const scale = cfg.maxVelocity / Math.sqrt(velSq);
      this.clampVector.set(vel.x * scale, vel.y * scale, vel.z * scale);
      body.setLinearVelocity(this.clampVector);
    }

    const angVelSq = angVel.lengthSquared();
    const maxAngVelSq = cfg.maxAngularVelocity * cfg.maxAngularVelocity;
    if (angVelSq > maxAngVelSq) {
      const scale = cfg.maxAngularVelocity / Math.sqrt(angVelSq);
      this.clampVector.set(angVel.x * scale, angVel.y * scale, angVel.z * scale);
      body.setAngularVelocity(this.clampVector);
    }

    // --- Time-based stop-to-rest
    const tiltMag = Math.sqrt(this.cachedTilt.x * this.cachedTilt.x + this.cachedTilt.y * this.cachedTilt.y);
    const planarSpeed = Math.sqrt(vel.x * vel.x + vel.z * vel.z);
    const angSpeed = Math.sqrt(angVel.x * angVel.x + angVel.z * angVel.z);
    const vertSpeed = Math.abs(vel.y);

    const isNearlyStill =
      tiltMag < cfg.stop.tilt &&
      planarSpeed < cfg.stop.planarSpeed &&
      angSpeed < cfg.stop.angularSpeed &&
      vertSpeed < 0.5; // Ball on board

    if (isNearlyStill) {
      this.nearStillTime += 1 / 60; // Approximate physics tick dt
      if (this.nearStillTime >= cfg.stop.time) {
        body.setLinearVelocity(this.zeroVector);
        body.setAngularVelocity(this.zeroVector);
      }
    } else {
      this.nearStillTime = 0;
    }
  }

  /** Check if physics is ready */
  get ready(): boolean {
    return this.initialized;
  }

  /** Create physics for the ball mesh */
  setupBall(mesh: Mesh): void {
    if (!this.initialized) {
      console.warn('Physics not initialized');
      return;
    }

    const { ball } = this.config;
    this.ballMesh = mesh;
    this.ballAggregate = new PhysicsAggregate(
      mesh,
      PhysicsShapeType.SPHERE,
      {
        mass: ball.mass,
        friction: ball.friction,
        restitution: ball.restitution,
      },
      this.scene
    );

    // Apply damping for realistic settling
    this.ballAggregate.body.setLinearDamping(ball.linearDamping);
    this.ballAggregate.body.setAngularDamping(ball.angularDamping);
  }

  /** Create static physics for the board surface */
  setupBoard(mesh: Mesh): void {
    if (!this.initialized) return;

    const { board } = this.config;
    this.boardAggregate = new PhysicsAggregate(
      mesh,
      PhysicsShapeType.BOX,
      {
        mass: 0, // Static
        friction: board.friction,
        restitution: board.restitution,
      },
      this.scene
    );
  }

  /** Create static physics for a wall */
  setupWall(mesh: Mesh): void {
    if (!this.initialized) return;

    const { wall } = this.config;
    const agg = new PhysicsAggregate(
      mesh,
      PhysicsShapeType.BOX,
      {
        mass: 0,
        friction: wall.friction,
        restitution: wall.restitution,
      },
      this.scene
    );
    this.wallAggregates.push(agg);
  }

  /** Create physics for a target bumper */
  setupTarget(mesh: Mesh, targetId: string): void {
    if (!this.initialized) return;

    const { target } = this.config;
    const agg = new PhysicsAggregate(
      mesh,
      PhysicsShapeType.CYLINDER,
      {
        mass: 0,
        friction: target.friction,
        restitution: target.restitution,
      },
      this.scene
    );

    // Enable collision detection on the TARGET, not the ball
    // This prevents the ball from reporting "ground hits" every frame
    agg.body.setCollisionCallbackEnabled(true);
    agg.body.getCollisionObservable().add(() => {
      // Emit target ID directly - no mesh name parsing needed
      this.onTargetHitObservable.notifyObservers(targetId);
    });

    this.targetAggregates.push(agg);
  }

  /** Create physics for the finish gate (blocks ball until opened) */
  setupFinishGate(mesh: Mesh): void {
    if (!this.initialized) return;

    this.finishMesh = mesh;
    this.finishAggregate?.dispose();
    this.finishAggregate = new PhysicsAggregate(
      mesh,
      PhysicsShapeType.BOX,
      { mass: 0, friction: 0.4, restitution: 0.2 },
      this.scene
    );
  }

  /** Open or close the finish gate physics */
  setFinishGateOpen(open: boolean): void {
    if (open) {
      // Remove physics body so ball can pass
      this.finishAggregate?.dispose();
      this.finishAggregate = null;
      return;
    }

    // Re-create physics body when closing
    if (!this.finishMesh || this.finishAggregate) return;

    this.finishAggregate = new PhysicsAggregate(
      this.finishMesh,
      PhysicsShapeType.BOX,
      { mass: 0, friction: 0.4, restitution: 0.2 },
      this.scene
    );
  }

  /**
   * Set tilt input for physics (call once per frame).
   * Caches tilt for use by physics observables and calculates boof.
   *
   * NOTE: Forces are now applied in onBeforePhysicsObservable,
   * and clamping happens in onAfterPhysicsObservable.
   */
  setTiltInput(tilt: Vec2, dt: number): void {
    if (dt <= 0) return;

    // Cache tilt for physics tick
    this.cachedTilt.x = tilt.x;
    this.cachedTilt.y = tilt.y;

    // --- Calculate boof (one-shot, queued for next physics tick)
    const { boof } = this.config;
    if (boof.enabled && !this.boofPending) {
      const rateX = (tilt.x - this.lastTilt.x) / dt;
      const rateY = (tilt.y - this.lastTilt.y) / dt;
      const rateMag = Math.sqrt(rateX * rateX + rateY * rateY);

      if (rateMag > boof.rateThreshold) {
        const extra = Math.min(
          (rateMag - boof.rateThreshold) * boof.strength,
          boof.maxKick
        );
        // Queue boof kick (signs flipped for pinball camera orientation)
        this.boofKickX = (-rateX / rateMag) * extra;
        this.boofKickZ = (rateY / rateMag) * extra;
        this.boofPending = true;
      }
    }

    this.lastTilt.x = tilt.x;
    this.lastTilt.y = tilt.y;
  }

  /** Get current ball position */
  getBallPosition(): Vector3 | null {
    return this.ballMesh?.position.clone() ?? null;
  }

  /** Get current ball velocity */
  getBallVelocity(): Vector3 | null {
    return this.ballAggregate?.body.getLinearVelocity() ?? null;
  }

  /** Get current ball angular velocity */
  getBallAngularVelocity(): Vector3 | null {
    return this.ballAggregate?.body.getAngularVelocity() ?? null;
  }

  /**
   * Compute slip ratio: how much the ball is sliding vs rolling.
   * Returns 0 if purely rolling, higher values indicate sliding.
   * slip = |ω_target - ω_actual| / max(ω_target, eps)
   */
  getSlipRatio(): number {
    if (!this.ballAggregate) return 0;

    const body = this.ballAggregate.body;
    const vel = body.getLinearVelocity();
    const angVel = body.getAngularVelocity();

    // Planar speed
    const planarSpeed = Math.sqrt(vel.x * vel.x + vel.z * vel.z);
    if (planarSpeed < 0.01) return 0; // At rest, no meaningful slip

    // Target angular velocity for pure rolling: ω = v / r
    const targetAngSpeed = planarSpeed / this.config.ball.radius;

    // Actual angular velocity magnitude (only XZ components matter for rolling)
    // For rolling: X velocity causes Z-axis rotation, Z velocity causes X-axis rotation
    const actualAngSpeed = Math.sqrt(angVel.x * angVel.x + angVel.z * angVel.z);

    // Slip ratio
    const eps = 0.1;
    return Math.abs(targetAngSpeed - actualAngSpeed) / Math.max(targetAngSpeed, eps);
  }

  /** Get planar speed (XZ plane only, for display) */
  getPlanarSpeed(): number {
    if (!this.ballAggregate) return 0;
    const vel = this.ballAggregate.body.getLinearVelocity();
    return Math.sqrt(vel.x * vel.x + vel.z * vel.z);
  }

  /** Stop ball and hide it (for fell state) */
  stopBall(): void {
    if (!this.ballAggregate || !this.ballMesh) return;

    const body = this.ballAggregate.body;
    body.setLinearVelocity(this.zeroVector);
    body.setAngularVelocity(this.zeroVector);

    // Allow direct mesh position changes to affect physics
    body.disablePreStep = true;
    this.ballMesh.position.y = -5;

    // Reset state
    this.nearStillTime = 0;
    this.boofPending = false;
  }

  /** Reset ball to spawn position */
  resetBall(spawnPos: Vector3): void {
    if (!this.ballAggregate || !this.ballMesh) return;

    const body = this.ballAggregate.body;

    // Stop all motion
    body.setLinearVelocity(this.zeroVector);
    body.setAngularVelocity(this.zeroVector);

    // Allow direct mesh position changes to affect physics
    body.disablePreStep = true;
    this.ballMesh.position.copyFrom(spawnPos);

    // Reset state
    this.nearStillTime = 0;
    this.boofPending = false;
    this.lastTilt.x = 0;
    this.lastTilt.y = 0;
    this.cachedTilt.x = 0;
    this.cachedTilt.y = 0;

    // Re-enable physics sync after one frame
    setTimeout(() => {
      body.disablePreStep = false;
    }, 0);
  }

  /** Check if ball is out of bounds (fell through drain or off table) */
  isBallOutOfBounds(table: ITableDef): boolean {
    const pos = this.getBallPosition();
    if (!pos) return false;

    const halfWidth = table.width / 2;
    const halfLength = table.length / 2;

    // Check if fallen below board
    if (pos.y < -0.5) return true;

    // Check if outside table bounds (with small buffer for ball radius)
    const buffer = 0.3;
    if (Math.abs(pos.x) > halfWidth + buffer) return true;
    if (Math.abs(pos.z) > halfLength + buffer) return true;

    return false;
  }

  /** Check if ball hit a target (simple distance check) */
  checkTargetHit(targetPos: Vector3, radius = 0.5): boolean {
    const ballPos = this.getBallPosition();
    if (!ballPos) return false;

    const dx = ballPos.x - targetPos.x;
    const dz = ballPos.z - targetPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    return dist < radius;
  }

  /** Check if ball fell in a hole */
  checkHoleFall(holePos: Vector3, radius: number): boolean {
    const ballPos = this.getBallPosition();
    if (!ballPos) return false;

    const dx = ballPos.x - holePos.x;
    const dz = ballPos.z - holePos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    // Ball center within hole radius
    return dist < radius * 0.8;
  }

  /** Clear all physics bodies (for table reload) */
  clearBodies(): void {
    // Dispose ball
    this.ballAggregate?.dispose();
    this.ballAggregate = null;
    this.ballMesh = null;

    // Dispose board
    this.boardAggregate?.dispose();
    this.boardAggregate = null;

    // Dispose walls
    for (const agg of this.wallAggregates) {
      agg.dispose();
    }
    this.wallAggregates = [];

    // Dispose targets
    for (const agg of this.targetAggregates) {
      agg.dispose();
    }
    this.targetAggregates = [];

    // Dispose finish gate
    this.finishAggregate?.dispose();
    this.finishAggregate = null;
    this.finishMesh = null;

    // Reset state
    this.nearStillTime = 0;
    this.boofPending = false;
  }

  dispose(): void {
    // Remove physics observable hooks
    if (this.beforePhysicsObserver) {
      this.scene.onBeforePhysicsObservable.remove(this.beforePhysicsObserver);
      this.beforePhysicsObserver = null;
    }
    if (this.afterPhysicsObserver) {
      this.scene.onAfterPhysicsObservable.remove(this.afterPhysicsObserver);
      this.afterPhysicsObserver = null;
    }

    this.clearBodies();
    this.onTargetHitObservable.clear();
    this.havokPlugin?.dispose();
  }
}
